import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { createNotification } from '@/lib/notifications';
import { emitDailyLogSync, unlinkDailyLogPhoto } from '@/lib/dailyLogUtils';
import { normalizeDailyLogId } from '@/lib/dailyLogShareDiagnostics';
import type { NewUploadFile } from '@/pages/management/daily-log/components/CreateDailyLogModal';

export interface DailyLogCreateInput {
  project_id: string;
  log_date: string;
  title?: string;
  management_comment?: string;
  internal_note?: string;
  visibility: 'internal' | 'client';
  is_published_to_client: boolean;
  selected_photo_ids: string[];
  created_by: string;
}

export interface DailyLogCreatePayload {
  input: DailyLogCreateInput;
  newFiles: NewUploadFile[];
  existingPhotoIds: string[];
}

export interface DailyLogUpdateInput extends DailyLogCreateInput {
  /** Present when edit modal submits; not used by updateDailyLog directly. */
  logId?: string;
}

export interface DailyLogUpdatePayload {
  dailyLogId: string;
  input: DailyLogCreateInput;
  newFiles: NewUploadFile[];
  existingPhotoIds: string[];
  removedLogPhotoIds: string[];
}

const MAX_FILE_SIZE = 20 * 1024 * 1024;
const DB_TIMEOUT_MS = 60000;
const STORAGE_TIMEOUT_MS = 60000;

function getFileExt(name: string): string {
  return name.split('.').pop()?.toLowerCase() || 'other';
}

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`${label} timed out after ${ms}ms`));
    }, ms);

    promise
      .then((value) => {
        clearTimeout(timer);
        resolve(value);
      })
      .catch((err) => {
        clearTimeout(timer);
        reject(err);
      });
  });
}

async function retryWithDelay<T>(
  fn: () => Promise<T>,
  retries: number,
  delayMs: number,
  label: string
): Promise<T> {
  let lastErr: unknown;
  for (let i = 0; i <= retries; i++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      console.warn(`[useDailyLogCreate] ${label} attempt ${i + 1} failed:`, err instanceof Error ? err.message : String(err));
      if (i < retries) {
        await new Promise((r) => setTimeout(r, delayMs));
      }
    }
  }
  throw lastErr;
}

export function useDailyLogCreate() {
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const endLoading = useCallback(() => {
    try {
      setCreating(false);
    } catch {
      /* ignore */
    }
  }, []);

  const createDailyLog = useCallback(async (payload: DailyLogCreatePayload): Promise<{ success: boolean; dailyLogId?: string; error?: string }> => {
    const { input, newFiles, existingPhotoIds } = payload;

    setCreating(true);
    setError(null);

    try {
      // ── A. Validate inputs ──
      if (!input.project_id) {
        const msg = 'Please select a project';
        setError(msg);
        endLoading();
        return { success: false, error: msg };
      }
      if (!input.log_date) {
        const msg = 'Please select a date';
        setError(msg);
        endLoading();
        return { success: false, error: msg };
      }
      if (!input.created_by) {
        const msg = 'User authentication required. Please sign in again.';
        setError(msg);
        endLoading();
        return { success: false, error: msg };
      }

      console.log('[useDailyLogCreate] === Create Daily Log clicked ===', {
        projectId: input.project_id,
        date: input.log_date,
        title: input.title,
        visibility: input.visibility,
        newUploadCount: newFiles.length,
        selectedExistingPhotoCount: existingPhotoIds.length,
        isPublishedToClient: input.is_published_to_client,
        createdBy: input.created_by,
      });

      // ── B. Insert Daily Log ──
      const logInsertPayload = {
        project_id: input.project_id,
        log_date: input.log_date,
        title: input.title || null,
        management_comment: input.management_comment || null,
        internal_note: input.internal_note || null,
        visibility: input.visibility,
        is_published_to_client: input.is_published_to_client,
        published_at: input.is_published_to_client ? new Date().toISOString() : null,
        created_by: input.created_by,
      };

      console.log('[useDailyLogCreate] Daily Log insert payload', logInsertPayload);

      let dailyLogId: string | undefined;

      try {
        const insertFn = async () => {
          const insertPromise = supabase
            .from('jphph_daily_logs')
            .insert(logInsertPayload)
            .select('id');
          return await withTimeout(insertPromise, DB_TIMEOUT_MS, 'Daily Log insert');
        };

        const { data: insertedRows, error: insertErr } = await retryWithDelay(insertFn, 1, 2000, 'Daily Log insert');

        console.log('[useDailyLogCreate] Daily Log insert result', {
          rowCount: insertedRows?.length ?? 0,
          error: insertErr?.message ?? null,
          errorCode: insertErr?.code ?? null,
        });

        if (insertErr) {
          const msg = insertErr.message || 'Failed to create daily log';
          console.error('[useDailyLogCreate] Daily Log insert failed:', msg, insertErr);
          setError(msg);
          endLoading();
          return { success: false, error: msg };
        }

        if (insertedRows && insertedRows.length > 0 && insertedRows[0].id) {
          dailyLogId = insertedRows[0].id;
          console.log('[useDailyLogCreate] Daily Log created with DB-generated ID:', dailyLogId);
        }
      } catch (insertRaceErr) {
        const errMsg = insertRaceErr instanceof Error ? insertRaceErr.message : String(insertRaceErr);
        console.warn('[useDailyLogCreate] Daily Log insert threw:', errMsg);
        // If it timed out, try a fallback query in case the insert actually succeeded
        if (errMsg.includes('timed out')) {
          console.log('[useDailyLogCreate] Insert timed out — querying fallback to check if record was created');
          try {
            const { data: fallbackRows } = await withTimeout(
              supabase
                .from('jphph_daily_logs')
                .select('id')
                .eq('project_id', input.project_id)
                .eq('log_date', input.log_date)
                .eq('created_by', input.created_by)
                .order('created_at', { ascending: false })
                .limit(1),
              DB_TIMEOUT_MS,
              'Daily Log fallback query'
            );
            if (fallbackRows && fallbackRows.length > 0 && fallbackRows[0].id) {
              dailyLogId = fallbackRows[0].id;
              console.log('[useDailyLogCreate] Fallback query found daily log ID:', dailyLogId);
            }
          } catch (fallbackErr) {
            console.error('[useDailyLogCreate] Fallback query also failed:', fallbackErr);
          }
        }

        if (!dailyLogId) {
          const msg = errMsg.includes('timed out')
            ? 'Daily Log creation timed out. Please check your connection and try again.'
            : errMsg;
          setError(msg);
          endLoading();
          return { success: false, error: msg };
        }
      }

      if (!dailyLogId) {
        const msg = 'Daily Log creation failed — no ID was returned. Please try again.';
        setError(msg);
        endLoading();
        return { success: false, error: msg };
      }

      // ── C. Upload new photos ──
      const uploadedRecordIds: string[] = [];
      const uploadErrors: string[] = [];

      for (let idx = 0; idx < newFiles.length; idx++) {
        const fileItem = newFiles[idx];
        if (fileItem.file.size > MAX_FILE_SIZE) {
          const err = `${fileItem.file.name} exceeds ${(MAX_FILE_SIZE / 1024 / 1024).toFixed(0)} MB limit`;
          uploadErrors.push(err);
          continue;
        }

        const ext = getFileExt(fileItem.file.name);
        const safeName = fileItem.file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const logDate = input.log_date;
        const ts = Date.now();

        const primaryPath = `daily-log-photos/${input.project_id}/${logDate}/${ts}_${safeName}`;
        const fallbackPath = `project-photos/${input.project_id}/${ts}_${safeName}`;

        console.log('[useDailyLogCreate] Photo upload start', {
          index: idx,
          fileName: fileItem.file.name,
          fileSize: fileItem.file.size,
          fileType: fileItem.file.type,
          primaryPath,
          fallbackPath,
        });

        let publicUrl: string | null = null;
        let resolvedBucket = '';
        let resolvedPath = '';

        // Attempt primary bucket with timeout
        try {
          const { error: storageErr } = await withTimeout(
            supabase.storage.from('daily-log-photos').upload(primaryPath, fileItem.file, { upsert: false }),
            STORAGE_TIMEOUT_MS,
            `Storage upload daily-log-photos ${fileItem.file.name}`
          );
          if (!storageErr) {
            const { data: urlData } = supabase.storage.from('daily-log-photos').getPublicUrl(primaryPath);
            publicUrl = urlData?.publicUrl ?? null;
            resolvedBucket = 'daily-log-photos';
            resolvedPath = primaryPath;
          } else {
            console.warn('[useDailyLogCreate] Primary bucket failed:', storageErr.message, '— trying fallback');
          }
        } catch (primaryErr) {
          const msg = primaryErr instanceof Error ? primaryErr.message : String(primaryErr);
          console.warn('[useDailyLogCreate] Primary bucket threw:', msg);
        }

        // Attempt fallback bucket with timeout
        if (!publicUrl) {
          try {
            const { error: fallbackErr } = await withTimeout(
              supabase.storage.from('project-photos').upload(fallbackPath, fileItem.file, { upsert: false }),
              STORAGE_TIMEOUT_MS,
              `Storage upload project-photos ${fileItem.file.name}`
            );
            if (!fallbackErr) {
              const { data: urlData } = supabase.storage.from('project-photos').getPublicUrl(fallbackPath);
              publicUrl = urlData?.publicUrl ?? null;
              resolvedBucket = 'project-photos';
              resolvedPath = fallbackPath;
            } else {
              console.error('[useDailyLogCreate] Fallback bucket also failed:', fallbackErr.message);
            }
          } catch (fallbackErr) {
            const msg = fallbackErr instanceof Error ? fallbackErr.message : String(fallbackErr);
            console.error('[useDailyLogCreate] Fallback bucket threw:', msg);
          }
        }

        if (!publicUrl) {
          const err = `${fileItem.file.name}: storage upload failed`;
          console.error('[useDailyLogCreate] Both buckets failed for', fileItem.file.name);
          uploadErrors.push(err);
          continue;
        }

        console.log('[useDailyLogCreate] Photo upload success', {
          index: idx,
          fileName: fileItem.file.name,
          bucket: resolvedBucket,
          path: resolvedPath,
          publicUrl,
        });

        // ── Insert jphph_project_files record ──
        const filePayload = {
          project_id: input.project_id,
          file_name: fileItem.file.name,
          file_url: publicUrl,
          file_size: String(fileItem.file.size),
          file_type: ext,
          caption: fileItem.caption?.trim() || null,
          note: null,
          uploaded_by: input.created_by,
          is_visible: true,
          is_deleted: false,
          review_status: input.is_published_to_client ? 'approved_for_client' : 'internal_only',
          is_client_visible: input.is_published_to_client,
          daily_log_id: dailyLogId,
          log_date: input.log_date,
          uploaded_context: 'daily_log',
        };

        console.log('[useDailyLogCreate] Inserting project_files record', { index: idx, filePayload });

        let insertedFileId: string | null = null;
        try {
          const { data: insertedFile, error: dbFileErr } = await withTimeout(
            supabase.from('jphph_project_files').insert(filePayload).select().maybeSingle(),
            DB_TIMEOUT_MS,
            `Project files insert ${fileItem.file.name}`
          );

          if (dbFileErr || !insertedFile) {
            const dbErrMsg = dbFileErr?.message || 'unknown database error';
            console.error('[useDailyLogCreate] DB insert failed:', dbErrMsg, dbFileErr);
            uploadErrors.push(`${fileItem.file.name}: database insert failed — ${dbErrMsg}`);
            continue;
          }

          insertedFileId = insertedFile.id;
          uploadedRecordIds.push(insertedFileId);
          console.log('[useDailyLogCreate] Project files record inserted', {
            index: idx,
            insertedFileId,
          });
        } catch (dbErr) {
          const msg = dbErr instanceof Error ? dbErr.message : String(dbErr);
          console.error('[useDailyLogCreate] DB insert threw:', msg);
          uploadErrors.push(`${fileItem.file.name}: database insert threw — ${msg}`);
          continue;
        }

        // ── Link to jphph_daily_log_photos (legacy, non-fatal) ──
        try {
          const photoLinkPayload = {
            daily_log_id: dailyLogId,
            project_id: input.project_id,
            source_photo_id: insertedFileId,
            photo_url: publicUrl,
            caption: fileItem.caption?.trim() || null,
            uploaded_by: input.created_by,
          };

          console.log('[useDailyLogCreate] Inserting daily_log_photos', { index: idx, photoLinkPayload });

          const { error: photoLinkErr } = await withTimeout(
            supabase.from('jphph_daily_log_photos').insert(photoLinkPayload),
            DB_TIMEOUT_MS,
            `Daily log photos insert ${fileItem.file.name}`
          );

          if (photoLinkErr) {
            console.error('[useDailyLogCreate] daily_log_photos insert error (non-fatal):', photoLinkErr.message);
          }
        } catch (linkErr) {
          console.warn('[useDailyLogCreate] daily_log_photos link threw (non-fatal):', linkErr);
        }

        // ── Link to jphph_daily_log_files (canonical, non-fatal) ──
        try {
          const fileLinkPayload = {
            daily_log_id: dailyLogId,
            project_id: input.project_id,
            file_id: insertedFileId,
          };

          console.log('[useDailyLogCreate] Inserting daily_log_files', { index: idx, fileLinkPayload });

          const { error: fileLinkErr } = await withTimeout(
            supabase.from('jphph_daily_log_files').insert(fileLinkPayload),
            DB_TIMEOUT_MS,
            `Daily log files insert ${fileItem.file.name}`
          );

          if (fileLinkErr) {
            console.error('[useDailyLogCreate] daily_log_files insert error (non-fatal):', fileLinkErr.message);
          }
        } catch (linkErr) {
          console.warn('[useDailyLogCreate] daily_log_files link threw (non-fatal):', linkErr);
        }
      }

      console.log('[useDailyLogCreate] Photo upload batch complete', {
        dailyLogId,
        uploadedCount: uploadedRecordIds.length,
        failedCount: uploadErrors.length,
        errors: uploadErrors,
      });

      // ── D. Link existing selected photos to the daily log ──
      if (existingPhotoIds.length > 0) {
        console.log('[useDailyLogCreate] Linking existing photos', {
          dailyLogId,
          count: existingPhotoIds.length,
          ids: existingPhotoIds,
        });

        let sourcePhotos: Array<{ id: string; file_url: string; caption: string | null; project_id: string }> = [];
        try {
          const { data: fetchedPhotos, error: sourceErr } = await withTimeout(
            supabase
              .from('jphph_project_files')
              .select('id, file_url, caption, project_id')
              .in('id', existingPhotoIds),
            DB_TIMEOUT_MS,
            'Fetch source photos'
          );
          if (sourceErr) {
            console.error('[useDailyLogCreate] Failed to fetch source photos:', sourceErr.message);
          } else {
            sourcePhotos = fetchedPhotos || [];
          }
        } catch (fetchErr) {
          console.error('[useDailyLogCreate] Fetch source photos threw:', fetchErr);
        }

        if (sourcePhotos.length > 0) {
          // Insert into jphph_daily_log_photos (legacy)
          try {
            const photoInserts = sourcePhotos.map((p) => ({
              daily_log_id: dailyLogId,
              project_id: p.project_id || input.project_id,
              source_photo_id: p.id,
              photo_url: p.file_url,
              caption: p.caption || null,
              uploaded_by: input.created_by,
            }));

            const { error: photoErr } = await withTimeout(
              supabase.from('jphph_daily_log_photos').insert(photoInserts),
              DB_TIMEOUT_MS,
              'Link existing to daily_log_photos'
            );

            if (photoErr) {
              console.error('[useDailyLogCreate] Failed to link photos (jphph_daily_log_photos):', photoErr.message);
            } else {
              console.log('[useDailyLogCreate] Linked to daily_log_photos:', photoInserts.length);
            }
          } catch (linkErr) {
            console.warn('[useDailyLogCreate] daily_log_photos link threw (non-fatal):', linkErr);
          }

          // Insert into jphph_daily_log_files (canonical)
          try {
            const fileLinkInserts = sourcePhotos.map((p) => ({
              daily_log_id: dailyLogId,
              project_id: p.project_id || input.project_id,
              file_id: p.id,
            }));

            const { error: fileLinkErr } = await withTimeout(
              supabase.from('jphph_daily_log_files').insert(fileLinkInserts),
              DB_TIMEOUT_MS,
              'Link existing to daily_log_files'
            );

            if (fileLinkErr) {
              console.error('[useDailyLogCreate] Failed to link files (jphph_daily_log_files):', fileLinkErr.message);
            } else {
              console.log('[useDailyLogCreate] Linked to daily_log_files:', fileLinkInserts.length);
            }
          } catch (linkErr) {
            console.warn('[useDailyLogCreate] daily_log_files link threw (non-fatal):', linkErr);
          }
        }

        // Update source photos to reference the daily log (non-fatal, best-effort)
        for (const photoId of existingPhotoIds) {
          try {
            await withTimeout(
              supabase
                .from('jphph_project_files')
                .update({
                  daily_log_id: dailyLogId,
                  is_client_visible: input.is_published_to_client,
                  review_status: input.is_published_to_client ? 'approved_for_client' : 'internal_only',
                })
                .eq('id', photoId),
              DB_TIMEOUT_MS,
              `Update source photo ${photoId}`
            );
          } catch (err) {
            console.warn('[useDailyLogCreate] Source photo update warning (non-fatal):', err);
          }
        }
      }

      // ── E. Create notification for client if published ──
      if (input.is_published_to_client) {
        try {
          const { data: project } = await withTimeout(
            supabase
              .from('jphph_projects')
              .select('client_user_id, project_name')
              .eq('id', input.project_id)
              .maybeSingle(),
            DB_TIMEOUT_MS,
            'Fetch project for notification'
          );

          if (project && (project as { client_user_id?: string }).client_user_id) {
            console.log('[useDailyLogCreate] Sending client notification');
            const notifResult = await withTimeout(
              createNotification({
                user_id: (project as { client_user_id: string }).client_user_id,
                project_id: input.project_id,
                project_name: (project as { project_name: string }).project_name,
                notification_type: 'update',
                title: `New Daily Log: ${input.title || input.log_date}`,
                body: input.management_comment?.slice(0, 200) || 'New site photos and updates are available.',
                target_role: 'client',
                link_path: '/client/portal?tab=Daily+Log',
                related_table: 'jphph_daily_logs',
                related_id: dailyLogId,
                created_by: input.created_by,
              }),
              DB_TIMEOUT_MS,
              'Create notification'
            );
            console.log('[useDailyLogCreate] Notification result:', notifResult);
          }
        } catch (notifErr) {
          console.warn('[useDailyLogCreate] Notification failed (non-fatal):', notifErr);
        }
      }

      // ── F. Final result ──
      const viewPath = `/management/projects/${input.project_id}?tab=Daily+Log`;

      console.log('[useDailyLogCreate] === Create Daily Log final result ===', {
        success: true,
        dailyLogId,
        viewPath,
        uploadedCount: uploadedRecordIds.length,
        attachedExistingCount: existingPhotoIds.length,
        failedCount: uploadErrors.length,
        errors: uploadErrors.length > 0 ? uploadErrors : null,
      });

      // If every new upload failed and there were new files, report failure but still created the log
      if (uploadErrors.length > 0 && uploadedRecordIds.length === 0 && newFiles.length > 0) {
        const errorMsg = `All ${newFiles.length} photo upload(s) failed: ${uploadErrors.join('; ')}`;
        setError(errorMsg);
        endLoading();
        return { success: true, dailyLogId, error: errorMsg };
      }

      endLoading();
      emitDailyLogSync(input.project_id);
      return { success: true, dailyLogId };
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('[useDailyLogCreate] UNCAUGHT ERROR in createDailyLog:', msg, err);
      setError(msg);
      endLoading();
      return { success: false, error: msg };
    } finally {
      try {
        console.log('[useDailyLogCreate] Setting creating=false in finally block');
        setCreating(false);
      } catch (finallyErr) {
        console.error('[useDailyLogCreate] CRITICAL: finally block itself threw:', finallyErr);
      }
    }
  }, [endLoading]);

  const updateDailyLog = useCallback(async (payload: DailyLogUpdatePayload): Promise<{ success: boolean; error?: string }> => {
    const { dailyLogId: rawDailyLogId, input, newFiles, existingPhotoIds, removedLogPhotoIds } = payload;

    setCreating(true);
    setError(null);

    try {
      const dailyLogId = normalizeDailyLogId(rawDailyLogId);
      if (!dailyLogId) {
        const msg = 'Daily Log ID could not be resolved. The update was not sent.';
        console.warn('[useDailyLogCreate] updateDailyLog blocked: invalid dailyLogId', {
          dailyLogId: rawDailyLogId,
          projectId: input?.project_id,
          logDate: input?.log_date,
        });
        setError(msg);
        endLoading();
        return { success: false, error: msg };
      }

      if (!input.project_id || !input.log_date || !input.created_by) {
        const msg = 'Missing required fields for update';
        setError(msg);
        endLoading();
        return { success: false, error: msg };
      }

      const updatePayload = {
        log_date: input.log_date,
        title: input.title || null,
        management_comment: input.management_comment || null,
        internal_note: input.internal_note || null,
        visibility: input.visibility,
        is_published_to_client: input.is_published_to_client,
        published_at: input.is_published_to_client ? new Date().toISOString() : null,
        updated_at: new Date().toISOString(),
      };

      const { error: updateErr } = await supabase
        .from('jphph_daily_logs')
        .update(updatePayload)
        .eq('id', dailyLogId);

      if (updateErr) {
        setError(updateErr.message);
        endLoading();
        return { success: false, error: updateErr.message };
      }

      for (const photoLinkId of removedLogPhotoIds) {
        await unlinkDailyLogPhoto(photoLinkId);
      }

      for (let idx = 0; idx < newFiles.length; idx++) {
        const fileItem = newFiles[idx];
        if (fileItem.file.size > MAX_FILE_SIZE) continue;

        const safeName = fileItem.file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const ts = Date.now();
        const primaryPath = `daily-log-photos/${input.project_id}/${input.log_date}/${ts}_${safeName}`;

        let publicUrl: string | null = null;
        try {
          const { error: storageErr } = await withTimeout(
            supabase.storage.from('daily-log-photos').upload(primaryPath, fileItem.file, { upsert: false }),
            STORAGE_TIMEOUT_MS,
            `Storage upload ${fileItem.file.name}`
          );
          if (!storageErr) {
            const { data: urlData } = supabase.storage.from('daily-log-photos').getPublicUrl(primaryPath);
            publicUrl = urlData?.publicUrl ?? null;
          }
        } catch {
          /* non-fatal */
        }

        if (!publicUrl) continue;

        const { data: fileRow } = await supabase
          .from('jphph_project_files')
          .insert({
            project_id: input.project_id,
            file_name: fileItem.file.name,
            file_url: publicUrl,
            file_type: fileItem.file.type || 'image/jpeg',
            uploaded_by: input.created_by,
            is_client_visible: input.is_published_to_client,
            review_status: input.is_published_to_client ? 'approved_for_client' : 'internal_only',
            daily_log_id: dailyLogId,
          })
          .select('id')
          .maybeSingle();

        const insertedFileId = fileRow?.id;
        if (!insertedFileId) continue;

        await supabase.from('jphph_daily_log_photos').insert({
          daily_log_id: dailyLogId,
          project_id: input.project_id,
          source_photo_id: insertedFileId,
          photo_url: publicUrl,
          caption: fileItem.caption?.trim() || null,
          uploaded_by: input.created_by,
        });

        await supabase.from('jphph_daily_log_files').insert({
          daily_log_id: dailyLogId,
          project_id: input.project_id,
          file_id: insertedFileId,
        });
      }

      if (existingPhotoIds.length > 0) {
        const { data: sourcePhotos } = await supabase
          .from('jphph_project_files')
          .select('id, file_url, caption, project_id')
          .in('id', existingPhotoIds);

        for (const p of sourcePhotos || []) {
          await supabase.from('jphph_daily_log_photos').insert({
            daily_log_id: dailyLogId,
            project_id: p.project_id || input.project_id,
            source_photo_id: p.id,
            photo_url: p.file_url,
            caption: p.caption || null,
            uploaded_by: input.created_by,
          });
          await supabase.from('jphph_daily_log_files').insert({
            daily_log_id: dailyLogId,
            project_id: p.project_id || input.project_id,
            file_id: p.id,
          });
        }
      }

      emitDailyLogSync(input.project_id);
      endLoading();
      return { success: true };
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      endLoading();
      return { success: false, error: msg };
    }
  }, [endLoading]);

  return { createDailyLog, updateDailyLog, creating, error };
}